import { create } from 'zustand';
import type {
  LanguageCode,
  TagType,
  ThematicState,
  EconomicSectorState,
  GrammaticalReferenceState,
  TagExtractionResponse,
  ThematicTagExtractionResponse,
  FileUploadMode,
} from '@/types/api';
import {
  getEconomicSectorDefaults,
  getGrammaticalReferenceDefaults,
} from '@/constants/defaultAllowedValues';

interface TagsState {
  thematic: ThematicState;
  economic_sector: EconomicSectorState;
  grammatical_reference: GrammaticalReferenceState;
}

interface TagsActions {
  // Thematic actions
  setThematicLessonMarkdown: (value: string) => void;
  setThematicLoading: (loading: boolean) => void;
  setThematicError: (error: string | null) => void;
  setThematicResponse: (response: ThematicTagExtractionResponse | null, traceId: string | null) => void;
  clearThematic: () => void;

  // Economic sector actions
  setEconomicSectorLessonMarkdown: (value: string) => void;
  setEconomicSectorValidValues: (values: string[]) => void;
  setEconomicSectorLoading: (loading: boolean) => void;
  setEconomicSectorError: (error: string | null) => void;
  setEconomicSectorResponse: (response: TagExtractionResponse | null, traceId: string | null) => void;
  loadEconomicSectorDefaults: () => void;
  applyEconomicSectorValuesFromFile: (mode: FileUploadMode, values: string[]) => void;
  clearEconomicSector: () => void;

  // Grammatical reference actions
  setGrammaticalReferenceLessonMarkdown: (value: string) => void;
  setGrammaticalReferenceLanguage: (language: LanguageCode) => void;
  setGrammaticalReferenceValidValues: (values: string[]) => void;
  setGrammaticalReferenceLoading: (loading: boolean) => void;
  setGrammaticalReferenceError: (error: string | null) => void;
  setGrammaticalReferenceResponse: (response: TagExtractionResponse | null, traceId: string | null) => void;
  loadGrammaticalReferenceDefaults: () => void;
  applyGrammaticalReferenceValuesFromFile: (mode: FileUploadMode, values: string[]) => void;
  clearGrammaticalReference: () => void;

  // Generic actions
  clearAll: () => void;
  loadFromHistory: (type: TagType, lessonMarkdown: string, validValues?: string[], language?: LanguageCode) => void;
}

type TagsStore = TagsState & TagsActions;

const initialThematicState: ThematicState = {
  lesson_markdown: '',
  loading: false,
  error: null,
  lastResponse: null,
  lastTraceId: null,
};

const initialEconomicSectorState: EconomicSectorState = {
  lesson_markdown: '',
  valid_values: [],
  loading: false,
  error: null,
  lastResponse: null,
  lastTraceId: null,
};

const initialGrammaticalReferenceState: GrammaticalReferenceState = {
  lesson_markdown: '',
  language: 'en',
  valid_values: [],
  loading: false,
  error: null,
  lastResponse: null,
  lastTraceId: null,
};

/**
 * De-duplicate values preserving order
 */
function deduplicateValues(values: string[]): string[] {
  const seen = new Set<string>();
  return values.filter((v) => {
    const trimmed = v.trim();
    if (seen.has(trimmed) || !trimmed) return false;
    seen.add(trimmed);
    return true;
  });
}

/**
 * Tags store - manages form state for all three tag extraction types
 */
export const useTagsStore = create<TagsStore>()((set, get) => ({
  // Initial state
  thematic: { ...initialThematicState },
  economic_sector: { ...initialEconomicSectorState },
  grammatical_reference: { ...initialGrammaticalReferenceState },

  // Thematic actions
  setThematicLessonMarkdown: (value) => {
    set((state) => ({
      thematic: { ...state.thematic, lesson_markdown: value },
    }));
  },

  setThematicLoading: (loading) => {
    set((state) => ({
      thematic: { ...state.thematic, loading },
    }));
  },

  setThematicError: (error) => {
    set((state) => ({
      thematic: { ...state.thematic, error, loading: false },
    }));
  },

  setThematicResponse: (response, traceId) => {
    set((state) => ({
      thematic: {
        ...state.thematic,
        lastResponse: response,
        lastTraceId: traceId,
        loading: false,
        error: null,
      },
    }));
  },

  clearThematic: () => {
    set({ thematic: { ...initialThematicState } });
  },

  // Economic sector actions
  setEconomicSectorLessonMarkdown: (value) => {
    set((state) => ({
      economic_sector: { ...state.economic_sector, lesson_markdown: value },
    }));
  },

  setEconomicSectorValidValues: (values) => {
    set((state) => ({
      economic_sector: { ...state.economic_sector, valid_values: values },
    }));
  },

  setEconomicSectorLoading: (loading) => {
    set((state) => ({
      economic_sector: { ...state.economic_sector, loading },
    }));
  },

  setEconomicSectorError: (error) => {
    set((state) => ({
      economic_sector: { ...state.economic_sector, error, loading: false },
    }));
  },

  setEconomicSectorResponse: (response, traceId) => {
    set((state) => ({
      economic_sector: {
        ...state.economic_sector,
        lastResponse: response,
        lastTraceId: traceId,
        loading: false,
        error: null,
      },
    }));
  },

  loadEconomicSectorDefaults: () => {
    const defaults = getEconomicSectorDefaults();
    set((state) => ({
      economic_sector: { ...state.economic_sector, valid_values: defaults },
    }));
  },

  applyEconomicSectorValuesFromFile: (mode, values) => {
    const cleanValues = deduplicateValues(values);
    set((state) => {
      const existing = state.economic_sector.valid_values;
      const newValues = mode === 'replace' ? cleanValues : deduplicateValues([...existing, ...cleanValues]);
      return {
        economic_sector: { ...state.economic_sector, valid_values: newValues },
      };
    });
  },

  clearEconomicSector: () => {
    set({ economic_sector: { ...initialEconomicSectorState } });
  },

  // Grammatical reference actions
  setGrammaticalReferenceLessonMarkdown: (value) => {
    set((state) => ({
      grammatical_reference: { ...state.grammatical_reference, lesson_markdown: value },
    }));
  },

  setGrammaticalReferenceLanguage: (language) => {
    set((state) => ({
      grammatical_reference: { ...state.grammatical_reference, language },
    }));
  },

  setGrammaticalReferenceValidValues: (values) => {
    set((state) => ({
      grammatical_reference: { ...state.grammatical_reference, valid_values: values },
    }));
  },

  setGrammaticalReferenceLoading: (loading) => {
    set((state) => ({
      grammatical_reference: { ...state.grammatical_reference, loading },
    }));
  },

  setGrammaticalReferenceError: (error) => {
    set((state) => ({
      grammatical_reference: { ...state.grammatical_reference, error, loading: false },
    }));
  },

  setGrammaticalReferenceResponse: (response, traceId) => {
    set((state) => ({
      grammatical_reference: {
        ...state.grammatical_reference,
        lastResponse: response,
        lastTraceId: traceId,
        loading: false,
        error: null,
      },
    }));
  },

  loadGrammaticalReferenceDefaults: () => {
    const { language } = get().grammatical_reference;
    const defaults = getGrammaticalReferenceDefaults(language);
    set((state) => ({
      grammatical_reference: { ...state.grammatical_reference, valid_values: defaults },
    }));
  },

  applyGrammaticalReferenceValuesFromFile: (mode, values) => {
    const cleanValues = deduplicateValues(values);
    set((state) => {
      const existing = state.grammatical_reference.valid_values;
      const newValues = mode === 'replace' ? cleanValues : deduplicateValues([...existing, ...cleanValues]);
      return {
        grammatical_reference: { ...state.grammatical_reference, valid_values: newValues },
      };
    });
  },

  clearGrammaticalReference: () => {
    set({ grammatical_reference: { ...initialGrammaticalReferenceState } });
  },

  // Generic actions
  clearAll: () => {
    set({
      thematic: { ...initialThematicState },
      economic_sector: { ...initialEconomicSectorState },
      grammatical_reference: { ...initialGrammaticalReferenceState },
    });
  },

  loadFromHistory: (type, lessonMarkdown, validValues, language) => {
    switch (type) {
      case 'thematic':
        set((state) => ({
          thematic: {
            ...state.thematic,
            lesson_markdown: lessonMarkdown,
            error: null,
            lastResponse: null,
            lastTraceId: null,
          },
        }));
        break;
      case 'economic_sector':
        set((state) => ({
          economic_sector: {
            ...state.economic_sector,
            lesson_markdown: lessonMarkdown,
            valid_values: validValues || [],
            error: null,
            lastResponse: null,
            lastTraceId: null,
          },
        }));
        break;
      case 'grammatical_reference':
        set((state) => ({
          grammatical_reference: {
            ...state.grammatical_reference,
            lesson_markdown: lessonMarkdown,
            language: language || 'en',
            valid_values: validValues || [],
            error: null,
            lastResponse: null,
            lastTraceId: null,
          },
        }));
        break;
    }
  },
}));
